import type { ShareFrame } from '~/api/types'

import type { Format } from './Frame'
import { frameFor } from './Frame'

/*
 * The cards the studio offers, which are the cards `_share_data` answers for.
 *
 * The browser's half of `calendar_server.SHARE_CARDS`: one entry per first path segment, by
 * the name the path spells rather than the name the payload uses — `e` and not `gig`, `v` and
 * not `room`, because `week/<n>` answers with a `gig` payload as well and a catalogue keyed
 * on the payload could not tell those two apart. `test_client_contract` reads both halves and
 * refuses to let them disagree, so a seventh card cannot ship unlisted and a template cannot
 * be listed that no route answers.
 *
 * Nothing here decides what a card *looks* like or which rows it draws. The frame comes from
 * `Frame`, the rows from the server; this names the six and says what each has to be given.
 */

/** What a template has to be given before it names a card. */
export type Need =
  /** Nothing: the route is the whole address. */
  | 'nothing'
  /** A night, which the route folds onto the week it falls in. Optional — this week. */
  | 'week'
  /** A night the window opens at. Optional — the route's own default reach. */
  | 'since'
  /** An event id, chosen through the site's search. */
  | 'gig'
  /** A room's slug, chosen the same way. */
  | 'venue'
  /** A number of pounds. */
  | 'pounds'

export interface Template {
  /** The first segment of the card's path, and the name the server knows it by. */
  kind: string
  /** What it is called on the page. */
  title: string
  /** One line: what goes on it. */
  what: string
  /** Which of the design's formats draws it. */
  format: Format
  /**
   * The frames this card is drawn in, **first one the default** — the browser's half of
   * `calendar_server.SHARE_FRAMES`, keyed the way that dict is: by the payload's kind, which
   * for the week's slide is `gig`. Two of the six take either shape; the other four state
   * theirs as a fact rather than offering a choice whose two answers draw the same card.
   *
   * The default being first is what makes the address without a `?frame=` the card it was
   * before frames existed — here and in `_share_frame_tail`, from one declaration each.
   */
  frames: readonly [ShareFrame, ...ShareFrame[]]
  /**
   * Whether this card draws a list of rows or a single one — the browser's half of
   * `calendar_server.SHARE_CHOSEN`, which is the five cards a draft may choose the rows of.
   *
   * `'one'` is not a card with a shorter list: *which* gig it is about has been its address
   * since it existed, so there is nothing on it to choose.
   */
  rows: 'list' | 'one'
  needs: Need
  /**
   * The week card alone takes a second argument: `week/<n>` is the carousel's nth slide,
   * which is one gig and therefore a different format from the cover. Optional on the
   * template because it is optional on the route — no slide is the cover.
   */
  slide?: { label: string; format: Format }
}

/**
 * The six, in the order they are offered.
 *
 * Typed as a non-empty tuple rather than an array, so the studio's opening template is
 * `TEMPLATES[0]` and not `TEMPLATES[0]` with a cast: `noUncheckedIndexedAccess` makes the
 * second `Template | undefined`, and a cast to silence that is a cast that would survive the
 * list becoming empty.
 */
export const TEMPLATES: readonly [Template, ...Template[]] = [
  {
    kind: 'week',
    title: 'The week',
    what: 'A marker a day, Monday to Sunday: a capsule where a day holds more than one, a day with nothing on it named in grey and keeping its place at half the height, and the room in the mono — in its own column on the post, under the name on the story. It tightens as more gigs are chosen, however many the box below offers.',
    format: 'A',
    frames: ['post', 'story'],
    rows: 'list',
    needs: 'week',
    slide: { label: 'Slide (1–7), for one night of it', format: 'B' },
  },
  {
    kind: 'e',
    title: 'One gig',
    what: 'The poster, and the night, room, time and price under it.',
    format: 'B',
    frames: ['post', 'story'],
    rows: 'one',
    needs: 'gig',
  },
  {
    kind: 'tonight',
    title: 'Tonight',
    what: "The three the front page leads with, on tonight's line.",
    format: 'E',
    frames: ['story'],
    rows: 'list',
    needs: 'nothing',
  },
  {
    kind: 'under',
    title: 'Under £N',
    what: "This week's picks below the number, cheapest first.",
    format: 'C',
    frames: ['post'],
    rows: 'list',
    needs: 'pounds',
  },
  {
    kind: 'v',
    title: 'A room',
    what: "One venue's coming nights, straight off its page.",
    format: 'D',
    frames: ['post'],
    rows: 'list',
    needs: 'venue',
  },
  {
    kind: 'announced',
    title: 'Just announced',
    what: 'The picks the calendar has only just seen, on the line of the ones it had.',
    format: 'J',
    frames: ['story'],
    rows: 'list',
    needs: 'since',
  },
]

/** What the editor has given a template. Blank, and the template's own frame, until he has. */
export interface Chosen {
  value: string
  slide: string
  /**
   * Which frame he pressed. Absent is not a third state: it is the template's own default,
   * which is what an address with no `?frame=` means, so "not chosen" and "chose the default"
   * are the same card and the same file by construction rather than by agreement.
   */
  frame?: ShareFrame
}

export const NOTHING: Chosen = { value: '', slide: '' }

/** Which frame this template is being drawn in: his, or the first one it declares. */
export function frameOf(card: Template, chosen: Chosen = NOTHING): ShareFrame {
  return chosen.frame ?? card.frames[0]
}

/**
 * The card's address after `/share/` — path and query together, in the order the route
 * family spells them.
 *
 * One function so the preview and the download cannot differ: the studio builds
 * `/share-page/<this>` and `/share/<this>` from the same string, which is the whole of the
 * "what you are looking at is what you get" property.
 *
 * `encodeURIComponent` on the segments and not on the query: an id or a slug is somebody
 * else's text and `_SHARE_KEY` will refuse whatever survives, while a night is this page's
 * own `<input type="date">` and encoding its hyphens would spell it differently from the
 * address the page prints.
 */
export function cardPath(card: Template, chosen: Chosen = NOTHING): string {
  const { value, slide } = chosen
  const query: string[] = []
  let path: string
  switch (card.needs) {
    case 'nothing':
      path = card.kind
      break
    case 'week': {
      const nth = slide ? `/${encodeURIComponent(slide)}` : ''
      path = `${card.kind}${nth}`
      if (value) query.push(`on=${value}`)
      break
    }
    case 'since':
      path = card.kind
      if (value) query.push(`since=${value}`)
      break
    default:
      path = `${card.kind}/${encodeURIComponent(value)}`
  }
  // The default frame is the address with no `?frame=` on it, which is the whole of why an
  // address that worked before frames existed still names the card it named — and why its
  // filename and its memo entry are the ones it had. `_share_frame_tail` is the same rule on
  // the server, over the same "first is the default" declaration.
  const frame = frameOf(card, chosen)
  if (frame !== card.frames[0]) query.push(`frame=${frame}`)
  return query.length > 0 ? `${path}?${query.join('&')}` : path
}

/** The first segment of a chosen card's address: `calendar_server.SHARE_DRAFT`'s other half. */
export const DRAFT = 'd'

/**
 * The card's own address with neither the frame nor the week's slide on it — what the pool is
 * asked for, and what a draft row stores as the card it is a selection for.
 *
 * Both are left off because neither is part of *which rows*: the same seven picks are the same
 * seven picks in either shape and on whichever slide is being looked at, and a chosen set that
 * had to be written again to change shape would be two rows for one choice.
 *
 * The slide is the server's rule as much as this one — `_share_draft_card` takes it off the
 * card a draft stores, and `SHARE_DRAFT_QUERY` is why. Two consequences here: the pool asked
 * for is the week's own seven nights whichever slide is on screen, and this string not moving
 * when the slide does is what lets `Desk` keep the draft across a change of slide alone.
 */
export function cardRows(card: Template, chosen: Chosen = NOTHING): string {
  return cardPath(card, { value: chosen.value, slide: '' })
}

/**
 * Where a chosen card is drawn: `d/<token>`, and the address's own share of the choices.
 *
 * The slide is here and not on the row, and so is the frame: **a bounded enumerable choice goes
 * in the address; anything a stranger must not be able to mint goes on the row.** The week's
 * slide has seven values and the frame two, and neither can name a card the editor's row did
 * not already name — where an id list in a query would be an unbounded key space on a public
 * renderer. `SHARE_DRAFT_QUERY` is the server's half of exactly this sentence.
 */
export function draftPath(card: Template, chosen: Chosen, token: string): string {
  const query: string[] = []
  if (chosen.slide) query.push(`slide=${encodeURIComponent(chosen.slide)}`)
  const frame = frameOf(card, chosen)
  if (frame !== card.frames[0]) query.push(`frame=${frame}`)
  return `${DRAFT}/${encodeURIComponent(token)}${query.length > 0 ? `?${query.join('&')}` : ''}`
}

/**
 * Whether this template has what it needs to name a card at all.
 *
 * The week and the just-announced card default to this week and to the route's own reach, so
 * they are ready with nothing typed; the other three name no card without their argument, and
 * asking for one would be a 404 the editor did not cause.
 *
 * What it does *not* check is whether the value is any good: 1–999 for the amount, a week the
 * calendar reaches, an id that exists. Those are the route's rules, the route says them in its
 * own words, and a second copy here would be a second set of them to keep in step.
 */
export function ready(card: Template, chosen: Chosen = NOTHING): boolean {
  return card.needs === 'nothing' || card.needs === 'week' || card.needs === 'since'
    ? true
    : chosen.value.trim() !== ''
}

/** Which format a template draws with what it has been given: the week's slide is a gig. */
export function formatOf(card: Template, chosen: Chosen = NOTHING): Format {
  return card.slide && chosen.slide.trim() !== '' ? card.slide.format : card.format
}

/**
 * One frame, as the page states it: `1080×1350 post`.
 *
 * The numbers come out of `Frame`'s `POST`/`STORY` through `frameFor`, and the word is the
 * frame's own name — the one the address and the payload use — so the page cannot state a size
 * the card is not drawn at and cannot spell a frame the route would refuse.
 *
 * `frame` defaults to the one this template is being drawn in, which is what the download
 * button says. The studio passes each of `card.frames` in turn to label a choice.
 */
export function frameLine(
  card: Template,
  chosen: Chosen = NOTHING,
  frame: ShareFrame = frameOf(card, chosen),
): string {
  const { width, height } = frameFor(formatOf(card, chosen), frame)
  return `${width}×${height} ${frame}`
}
